import { useQuery } from '@tanstack/react-query';
import { Clock, FileText } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { useDestinationWindow } from '@/lib/window';
import { PhoneFrame } from '@/components/PlaygroundTester';

// Testar o PRÓPRIO template sem escrever JSON: escolhe o aprovado, preenche as
// variáveis e vê a mensagem como o contato vai ver. Tudo lê e escreve no corpo da
// requisição (payload), para o snippet de código mostrar exatamente o que sai.

interface Tpl {
  id: string; name: string; language: string; category: string; status: string; variables: number;
  spec?: { body?: { text?: string }; header?: { type?: string; text?: string }; footer?: string;
    buttons?: { type: string; text?: string; example?: string }[] };
}

type Body = Record<string, unknown>;

function parse(payload: string): Body {
  try { const o = JSON.parse(payload); return o && typeof o === 'object' ? o as Body : {}; } catch { return {}; }
}

/**
 * Nomes das variáveis na ordem em que aparecem — numeradas ({{1}}) ou nomeadas
 * ({{nome}}). É a mesma ordem em que os valores vão no envio.
 */
function varNames(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi)) if (!out.includes(m[1])) out.push(m[1]);
  return /^\d+$/.test(out[0] ?? '') ? out.sort((a, b) => Number(a) - Number(b)) : out;
}

/** Corpo com as variáveis trocadas pelo que foi digitado (vazias ficam visíveis). */
function render(text: string, vars: string[]): string {
  const names = varNames(text);
  return text.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (m, n) => vars[names.indexOf(n)]?.trim() || m);
}

export function PlaygroundTemplatePicker({ payload, onPayload, businessName }: { payload: string; onPayload: (p: string) => void; businessName?: string }) {
  const t = useT();
  const { data: list = [] } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => ((await apiFetch('GET', '/templates')).data as { templates?: Tpl[] })?.templates ?? [],
  });
  const approved = list.filter((x) => x.status === 'APPROVED');
  const body = parse(payload);
  const tpl = approved.find((x) => x.name === body.template && (!body.language || x.language === body.language));
  const vars = Array.isArray(body.variables) ? (body.variables as unknown[]).map(String) : [];
  const isAuth = tpl?.category === 'AUTHENTICATION';
  const names = varNames(tpl?.spec?.body?.text ?? '');
  // Botão de copiar cupom: a Meta exige o cupom em todo envio. Vazio, vai o
  // cupom cadastrado no template.
  const couponIdx = (tpl?.spec?.buttons ?? []).findIndex((b) => b.type === 'copy_code');
  const couponBtn = couponIdx >= 0 ? tpl?.spec?.buttons?.[couponIdx] : undefined;
  const buttonVars = Array.isArray(body.button_variables) ? (body.button_variables as unknown[]).map(String) : [];
  const setCoupon = (v: string) => {
    const bv = Array.from({ length: Math.max(buttonVars.length, couponIdx + 1) }, (_, j) => buttonVars[j] ?? '');
    bv[couponIdx] = v;
    const next: Body = { ...body };
    if (bv.every((x) => x === '')) delete next.button_variables; else next.button_variables = bv;
    write(next);
  };

  const write = (next: Body) => onPayload(JSON.stringify(next, null, 2));

  const pick = (id: string) => {
    const x = approved.find((a) => a.id === id);
    if (!x) return;
    const next: Body = { to: body.to ?? '', template: x.name, language: x.language };
    if (body.instance_id) next.instance_id = body.instance_id;
    if (x.category === 'AUTHENTICATION') next.code = '';
    else if (x.variables > 0) next.variables = Array.from({ length: x.variables }, () => '');
    write(next);
  };

  const setVar = (i: number, v: string) => {
    const nv = Array.from({ length: tpl?.variables ?? vars.length }, (_, j) => vars[j] ?? '');
    nv[i] = v;
    write({ ...body, variables: nv });
  };

  if (approved.length === 0) {
    return <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">{t('playground.template.none')}</p>;
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border bg-muted/30 p-4">
      <div className="flex flex-col gap-1.5">
        <Label>{t('playground.template.pick')}</Label>
        <select
          value={tpl?.id ?? ''}
          onChange={(e) => pick(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">{t('playground.template.choose')}</option>
          {approved.map((x) => <option key={x.id} value={x.id}>{x.name} · {x.language} · {x.category}</option>)}
        </select>
      </div>

      {tpl && !isAuth && Array.from({ length: tpl.variables }, (_, i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <Label>
            {t('playground.template.variable', { n: i + 1 })}
            {/^\d+$/.test(names[i] ?? '') ? null : <code className="ml-1.5 text-xs text-muted-foreground">{`{{${names[i]}}}`}</code>}
          </Label>
          <Input value={vars[i] ?? ''} onChange={(e) => setVar(i, e.target.value)} className="h-9" />
        </div>
      ))}
      {tpl && !isAuth && couponBtn && (
        <div className="flex flex-col gap-1.5">
          <Label>{t('playground.template.coupon')}</Label>
          <Input value={buttonVars[couponIdx] ?? ''} onChange={(e) => setCoupon(e.target.value)}
            placeholder={couponBtn.example || ''} maxLength={20} className="h-9 font-mono" />
          <p className="text-xs text-muted-foreground">
            {couponBtn.example ? t('playground.template.couponDefault', { code: couponBtn.example }) : t('playground.template.couponRequired')}
          </p>
        </div>
      )}
      {tpl && isAuth && (
        <div className="flex flex-col gap-1.5">
          <Label>{t('playground.template.code')}</Label>
          <Input value={String(body.code ?? '')} onChange={(e) => write({ ...body, code: e.target.value })} className="h-9 font-mono" />
        </div>
      )}

      {tpl?.spec?.body?.text && (
        <div className="flex flex-col gap-1.5">
          <Label>{t('playground.template.preview')}</Label>
          <PhoneFrame name={businessName}>
            <div className="flex w-[85%] flex-col gap-px self-start">
            <div className="rounded-lg rounded-tl-none bg-white px-2.5 py-1.5 text-[13px] leading-snug text-[#111b21] shadow-sm dark:bg-[#202c33] dark:text-[#e9edef]">
              {tpl.spec.header?.text && <p className="mb-1 font-semibold">{render(tpl.spec.header.text, [])}</p>}
              {tpl.spec.header?.type && tpl.spec.header.type !== 'TEXT' && !tpl.spec.header.text && (
                <div className="-mx-1 mb-1 flex h-28 items-center justify-center rounded bg-black/5 text-xs uppercase text-black/40 dark:bg-white/5 dark:text-white/40">{tpl.spec.header.type}</div>
              )}
              <p className="whitespace-pre-wrap break-words">{isAuth ? render(tpl.spec.body.text, [String(body.code ?? '')]) : render(tpl.spec.body.text, vars)}</p>
              {tpl.spec.footer && <p className="mt-1 text-[11px] text-black/50 dark:text-white/50">{tpl.spec.footer}</p>}
            </div>
            {(tpl.spec.buttons ?? []).map((b, i) => (
              <div key={i} className="rounded-md bg-white px-3 py-1.5 text-center text-[13px] font-medium text-[#027eb5] shadow-sm dark:bg-[#202c33] dark:text-[#53bdeb]">
                {b.text || b.type}
              </div>
            ))}
            </div>
          </PhoneFrame>
        </div>
      )}
    </div>
  );
}

/** Estado da janela de 24h para o destino de teste (só no oficial). */
export function DestinationWindow({ phone }: { phone: string }) {
  const t = useT();
  const win = useDestinationWindow(phone);
  if (!win.known) return null;
  const left = Math.ceil(win.leftMs / 60_000);
  const time = left >= 60 ? `${Math.floor(left / 60)}h${String(left % 60).padStart(2, '0')}` : `${left}min`;
  return (
    <p className={'flex items-center gap-1.5 text-xs ' + (win.open ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400')}>
      {win.open ? <Clock className="size-3.5" /> : <FileText className="size-3.5" />}
      {win.open ? t('playground.window.open', { time }) : win.never ? t('playground.window.never') : t('playground.window.closed')}
    </p>
  );
}

/**
 * Trava do oficial: com a janela fechada, texto e mídia não chegam — a Meta só
 * aceita template. Em vez de deixar enviar e devolver 422, a tela explica e leva
 * ao template. Com template de alternativa no projeto, o envio sai por ele, então
 * só avisa.
 */
export function WindowGate({ blocked, fallbackName, onUseTemplate }: { blocked: boolean; fallbackName?: string; onUseTemplate: () => void }) {
  const t = useT();
  if (!blocked && !fallbackName) return null;
  return (
    <div className={'flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3 text-sm ' +
      (blocked ? 'border-destructive/30 bg-destructive/5' : 'border-amber-500/30 bg-amber-500/5')}>
      <span>{blocked ? t('playground.window.blocked') : t('playground.window.viaFallback', { name: fallbackName ?? '' })}</span>
      <button onClick={onUseTemplate} className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90">
        {t('playground.window.useTemplate')}
      </button>
    </div>
  );
}
